<template>
  <v-card>
    <v-card-title class="primary white--text headline">
      <Icon
      iconColor="tertiary"
      :small="false"
      :tooltipText="$t('ticketBooks')"
      icon="mdi-bookshelf"
      large/>
      {{ $t('ticketBooks') }}
      <v-spacer/>
      <BaseDialogActions hideRefresh/>
    </v-card-title>
    <v-card-text>
      <v-container fluid>
        <LoadableComponent :loading="loading" :height="525">
          <v-data-iterator
          :items="ticketBooksByAccount"
          :items-per-page="5">
            <template #header>
              <v-row class="px-2 py-5">
                <v-col cols="12">
                  <v-text-field
                  v-model.number="searchPage"
                  hide-details
                  dense
                  outlined
                  :label="$t('searchPageNumber')"
                  type="number"
                  append-icon="mdi-magnify"
                  hide-spin-buttons
                  clearable
                  ref="searchBox">
                    <template #append-outer>
                      <Icon
                      :icon="archiveFilter.icon"
                      :tooltipText="archiveFilter.tooltipText"
                      @icon-clicked="toggleArchive"
                      :small="false"
                      :overrideClickable="validSearchPage"
                      icon-color="error"/>
                      <Icon
                      icon="mdi-checkbox-multiple-marked-circle"
                      :tooltipText="validateTicketBookIconTooltip"
                      :small="false"
                      @icon-clicked="checkOverlap()"
                      :iconColor="validateTicketBookIconColor"
                      />
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </template>
            <template #default="{ items }">
              <v-expansion-panels popout tile v-model="openPanel" accordion active-class="whiteStroke" class="secondaryStroke">
                <v-expansion-panel v-for="item in items" :key="item.accountId">
                  <v-expansion-panel-header>
                    <v-container fluid class="pl-0">
                      <v-row>
                        <v-col cols="6">
                          {{ item.account.name }}
                        </v-col>
                        <v-spacer/>
                        <v-col cols="6" align="right">
                          {{ accountTicketBookCount(item) }}
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12">
                        <TicketBookTable
                        :ticketBooks="item.books"
                        :headers="ticketBookHeaders()"
                        readonly
                        :loading="false"
                        :searchedTicketIds="searchedTicketIds"
                        @view-pages="viewPages($event)"
                        @view-ticket="viewTicket($event)"/>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </template>
            <template #no-data>
              <v-container>
                <NoData>{{ $t('noTicketBooks') }}</NoData>
              </v-container>
            </template>
          </v-data-iterator>
        </LoadableComponent>
      </v-container>
    </v-card-text>
    <v-dialog v-model="viewDetailDialog" width="600px">
      <TicketBookDetail
      v-if="viewDetailDialog"
      :ticketBook="focusedTicketBook"
      :readonly="true"
      :accountId="focusedTicketBook?.accountId"
      :initialPageNumber="validSearchPage ? searchPage : undefined"
      @close="closeDialogs"/>
    </v-dialog>
    <TicketBookOverlapDialog
    :isOpen="showOverlapDialog"
    :overlapData="overlap"
    :ticketBooks="ticketBooks"
    @close="closeOverlapDialog"/>
    <Dialog :stateId="ticketDetailDialogId" maxWidth="80vw" @close="resetTicketDetailDialog">
      <TicketDetails
      v-if="ticketDetailDialog"
      :ticketId="focusedTicketId"/>
    </Dialog>
  </v-card>
</template>

<script>
import { ticketBookHeaders } from '@/headers/Account'
import { TicketBookStatus } from '@/utils/Enumerations'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'TicketBooks',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    TicketBookTable: () => import('@/components/account/ticketBooks/TicketBookTable.vue'),
    TicketBookDetail: () => import('@/components/account/ticketBooks/TicketBookDetail.vue'),
    TicketBookOverlapDialog: () => import('@/components/account/ticketBooks/TicketBookOverlapDialog.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    TicketDetails: () => import('@/components/ticket/ticket-details/TicketDetails.vue')
  },

  data: () => ({
    ticketBooks: [],
    openPanel: undefined,
    viewDetailDialog: false,
    focusedTicketBook: undefined,
    searchPage: undefined,
    loading: false,
    showArchived: false,
    validateTicketBookIconColor: 'primary',
    validateTicketBookIconTooltip: '',
    overlap: undefined,
    showOverlapDialog: false,
    hasCheckedOverlapCurrentSession: false,
    ticketDetailDialogId: uniqueDialogId('ticket-books-ticket-detail'),
    ticketDetailDialog: false,
    focusedTicketId: undefined,
    searchedTicketIds: {}
  }),

  watch: {
    searchPage () {
      clearTimeout(this.searchDebounce)
      this.searchedTicketIds = {}
      if (!this.validSearchPage || this.filteredTicketBooks.length === 0) return
      this.searchDebounce = setTimeout(this.fetchSearchedTicketIds, 300)
    },

    ticketBooksByAccount: {
      deep: true,
      immediate: true,
      handler () {
        if (this.ticketBooksByAccount.length !== 1) return
        this.$nextTick(_ => { this.openPanel = 0 })
      }
    }
  },

  async created () {
    this.searchDebounce = null
    this.loading = true
    this.validateTicketBookIconTooltip = this.$t('checkTicketBookOverlap')
    this.ticketBooks = await this.fetchTicketBooks({ includeInUse: true, includeArchived: true, includeAvailable: true, includePageNumbers: false })
    this.hasCheckedOverlapCurrentSession = false
    this.overlap = undefined
    this.validateTicketBookIconColor = 'primary'
    setTimeout(_ => {
      this.loading = false
      requestAnimationFrame(_ => this.$refs.searchBox.focus())
    }, 300) // Smooth out loading
  },

  beforeDestroy () {
    clearTimeout(this.searchDebounce)
  },

  computed: {
    ...mapGetters('account', ['allAccounts']),
    ticketBooksByAccount () {
      const groupedTicketBooks = Object.groupBy(this.filteredTicketBooks, ({ accountId }) => accountId)
      const ticketBooksByAccount = Object.entries(groupedTicketBooks).map(([accountId, books]) => {
        accountId = parseInt(accountId)
        return {
          accountId,
          account: this.allAccounts.find(a => a.accountId === accountId),
          books
        }
      })
      return ticketBooksByAccount.sort((a, b) => a.account.name.localeCompare(b.account.name))
    },

    filteredTicketBooks () {
      if (this.searchPage == null || this.searchPage === '') return this.ticketBooks.filter(b => this.showArchived || b.status !== TicketBookStatus.Inactive.value)
      const searchPage = parseInt(this.searchPage)
      return this.ticketBooks.filter(book => book.firstPage <= searchPage && book.lastPage >= searchPage)
    },

    validSearchPage () {
      return this.searchPage != null && this.searchPage !== ''
    },

    archiveFilter () {
      const toggle = this.showArchived || this.validSearchPage
      return {
        icon: toggle ? 'mdi-archive-outline' : 'mdi-archive',
        tooltipText: this.validSearchPage ? this.$t('showingAll') : this.showArchived ? this.$t('hideArchived') : this.$t('showArchived')
      }
    }
  },

  methods: {
    ticketBookHeaders,
    ...mapActions('ticket-books', ['fetchTicketBooks', 'fetchTicketBooksOverlap']),
    ...mapActions('dialog', ['openOrUpdateDialog']),
    accountTicketBookCount (item) {
      const count = item.books.length
      const label = count > 1 ? this.$t('ticketBooks') : this.$t('ticketBook')
      return `${count} ${label}`
    },

    closeDialogs () {
      this.viewDetailDialog = false
      this.focusedTicketBook = undefined
    },

    viewPages (book) {
      this.viewDetailDialog = true
      this.focusedTicketBook = book
    },

    async fetchSearchedTicketIds () {
      const pageNumber = this.searchPage
      const books = await this.fetchTicketBooks({ includeInUse: true, includeArchived: true, includeAvailable: true, includePageNumbers: true, pageNumbers: [pageNumber] })
      if (pageNumber !== this.searchPage) return
      this.searchedTicketIds = Object.fromEntries(books.flatMap(b => b.pages
        .filter(p => p.ticketId != null)
        .map(p => [b.ticketBookId, p.ticketId])))
    },

    viewTicket (ticketId) {
      this.focusedTicketId = ticketId
      this.ticketDetailDialog = true
      this.openOrUpdateDialog({ id: this.ticketDetailDialogId, allowFullscreen: true })
    },

    resetTicketDetailDialog () {
      this.ticketDetailDialog = false
      this.focusedTicketId = undefined
    },

    toggleArchive () {
      this.showArchived = !this.showArchived
    },

    closeOverlapDialog () {
      this.showOverlapDialog = false
    },

    async checkOverlap () {
      if (!this.hasCheckedOverlapCurrentSession) {
        var response = await this.fetchTicketBooksOverlap()
        this.overlap = response
        this.hasCheckedOverlapCurrentSession = true
      }
      if (this.overlap?.length > 0) {
        this.validateTicketBookIconColor = 'error'
        this.validateTicketBookIconTooltip = this.$t('collisionDetected')
        this.showOverlapDialog = true
      } else {
        this.validateTicketBookIconColor = 'success'
        this.validateTicketBookIconTooltip = this.$t('noCollisions')
      }
      return this.overlap
    }
  }
}
</script>
